import { useTranslation } from 'react-i18next';

import { formatToMaxFourDigits, IconWrapper } from '@flexo/general';
import { RIGHT_DRAWER, store } from '@flexo/reducers';
import {
  isValidKwickInvoice,
  transformRealDataToStructuredData,
} from 'libs/atoms/src/lib/table/helpers';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PaymentsListSkeleton } from './payments-list-skeleton';
import styles from './payments-list.module.scss';

export type PaymentsListType =
  | 'communityPaymentsList'
  | 'sitePaymentsList'
  | 'memberPaymentsList';

export interface PaymentsListProps {
  type: PaymentsListType;
  data?: any;
}

export function PaymentsList({ type, data }: PaymentsListProps) {
  const { t } = useTranslation();
  const dispatch = store.dispatch;
  const kpi = useSelector((state: any) => state.kpi);
  const metadata = useSelector((state: any) => state.metadata);
  const [kpiData, setKpiData] = useState<any[]>([]);

  // Track entity changes to clear data immediately
  const previousEntityIdRef = useRef<string | null>(null);
  const currentEntityId = metadata?.selectedEntity?.id || null;

  // Check if invoices are loading
  const isLoading = kpi?.KwickInvoicesAsyncStatus === 'PENDING';

  // Clear data immediately when entity changes
  useEffect(() => {
    if (
      previousEntityIdRef.current !== null &&
      previousEntityIdRef.current !== currentEntityId
    ) {
      // Entity changed - clear the data immediately
      setKpiData([]);
    }
    previousEntityIdRef.current = currentEntityId;
  }, [currentEntityId]);

  // Transform and set invoice data when available
  const invoices = kpi?.kwickInvoices;
  useEffect(() => {
    if (invoices && Array.isArray(invoices) && invoices.length > 0) {
      const _transformedData = transformRealDataToStructuredData(
        invoices,
        metadata
      );
      const _formattedData = _transformedData?.map((period: any) => {
        return {
          ...period,
          period: period.month,
        };
      });

      setKpiData(_formattedData);
    } else {
      setKpiData([]);
    }
  }, [invoices, metadata]);

  // Show skeleton while loading
  if (isLoading) {
    return <PaymentsListSkeleton title={data?.title} />;
  }

  // Show empty state when no data
  if (!kpiData?.length) {
    return (
      <div className={styles.Payments__List}>
        <p className={styles.Payments__List__Title__Loading}>{t(data.title)}</p>
        <div className={styles.Payments__List__Content}>
          {t('widgets.tables.InvoicesPeriodTable.no_data_text')}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Payments__List}>
      <p
        className={styles.Payments__List__Title}
        onClick={() => {
          if (kpiData?.length < 1) {
            return;
          }

          dispatch(
            RIGHT_DRAWER.SetDrawer({
              type: 'COMMUNITY_PAYMENTS_LIST',
              show: true,
              drawerConfig: {
                title: t('widgets.tables.InvoicesPeriodTable.title', {
                  communityName: metadata.selectedEntity?.communityName,
                }),
                subtitle: t('widgets.tables.InvoicesPeriodTable.subtitle'),
                data: kpiData,
              },
            } as any) as any
          );
        }}
      >
        {t(data.title)}
        <IconWrapper iconName="chevron-right" />
      </p>
      <div className={styles.Payments__List__Content}>
        {(kpiData || [])
          ?.filter((_, invoiceIndex) => invoiceIndex < 4)
          ?.map((period: any, periodIndex: number) => {
            const validSites = (period?.sites || []).filter(
              isValidKwickInvoice
            );
            const _paidSites = validSites.filter((site: any) => site?.paid);
            const _totalSites = validSites.length;
            const _allSitesPaid = _paidSites?.length === _totalSites;

            const _totalAmount = validSites.reduce(
              (acc: number, site: any) => acc + site?.amount,
              0
            );
            const _totalAmountPaid = validSites.reduce(
              (acc: number, site: any) => acc + (site?.paid ? site?.amount : 0),
              0
            );

            return (
              <div
                className={styles.Payments__List__Content__Period__Wrapper}
                key={`${data.title}-${periodIndex}`}
              >
                <div className={styles.Payments__List__Content__Period__Info}>
                  <p
                    className={styles.Payments__List__Content__Period__Title}
                    onClick={() => {
                      dispatch(
                        RIGHT_DRAWER.SetDrawer({
                          type: 'INVOICES_PERIOD_DETAILS',
                          show: true,
                          drawerConfig: {
                            title: t(
                              'widgets.tables.InvoicesPeriodTable.title',
                              {
                                communityName:
                                  metadata.selectedEntity?.communityName,
                              }
                            ),
                            subtitle: t(
                              'widgets.tables.InvoicesPeriodTable.subtitle'
                            ),
                            data: period,
                            oldData: {
                              data: kpiData,
                              title: t(
                                'widgets.tables.InvoicesPeriodTable.title',
                                {
                                  communityName:
                                    metadata.selectedEntity?.communityName,
                                }
                              ),
                              subtitle: t(
                                'widgets.tables.InvoicesPeriodTable.subtitle'
                              ),
                            },
                          },
                        } as any) as any
                      );
                    }}
                  >
                    {period?.period}
                  </p>
                  <p className={styles.Payments__List__Content__Period__Status}>
                    {_paidSites?.length}/{_totalSites} {t(data?.paid)}
                  </p>
                </div>
                <p className={styles.Payments__List__Content__Period__Amount}>
                  {_allSitesPaid && (
                    <IconWrapper iconName="check-encircled-secondary" />
                  )}
                  {period?.sites?.[0]?.unit ? ` ${period.sites[0].unit} ` : ''}
                  {_totalAmountPaid === _totalAmount
                    ? formatToMaxFourDigits(_totalAmountPaid, true)
                    : `${formatToMaxFourDigits(
                        _totalAmountPaid,
                        true
                      )}/${formatToMaxFourDigits(_totalAmount, true)}`}
                </p>
              </div>
            );
          })}
      </div>
    </div>
  );
}
